import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, MapPin, Users, Trophy, Search, Filter, Star, UserCheck, CheckCircle2 } from 'lucide-react';
import { TeamLogo } from '@/components/games/TeamLogo';
import { venueGamesService, VenueGame } from '@/services/venueGamesService';
import { gamesService, Game } from '@/services/gamesService';
import { sportsService, Sport } from '@/services/sportsService';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { PlayerRequestNotifications } from '@/components/notifications/PlayerRequestNotifications';
import { toast } from 'sonner';

interface GameFilters {
  sport_id?: number;
  date_from?: string;
  date_to?: string;
  location?: string;
  status?: string;
}

export const GameDiscoveryInterface = () => {
  const [venueGames, setVenueGames] = useState<VenueGame[]>([]);
  const [regularGames, setRegularGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<GameFilters>({});
  const [availableSports, setAvailableSports] = useState<Sport[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [userGameRequests, setUserGameRequests] = useState<{[gameId: string]: {[teamSide: string]: boolean}}>({});
  const [requestingGames, setRequestingGames] = useState<{[key: string]: boolean}>({});
  
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const { permissions, userId } = useUserRole();
  const user = supabaseUser || internalUser;

  useEffect(() => {
    loadAvailableGames();
    loadSports();
    if (userId) {
      loadUserGameRequests();
    }
  }, [filters, userId]);

  const loadUserGameRequests = async () => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('game_participants')
        .select('game_id, team_side, participation_status')
        .eq('player_id', userId)
        .eq('participation_status', 'requested');
      
      if (!error && data) {
        const requestsMap: {[gameId: string]: {[teamSide: string]: boolean}} = {};
        data.forEach(request => {
          if (!requestsMap[request.game_id]) {
            requestsMap[request.game_id] = {};
          }
          requestsMap[request.game_id][request.team_side] = true;
        });
        setUserGameRequests(requestsMap);
      }
    } catch (error) {
      console.error('Error loading user game requests:', error);
    }
  };

  const loadAvailableGames = async () => {
    try {
      setLoading(true);
      
      // Load both venue games and regular games
      const [venueGamesData, regularGamesData] = await Promise.all([
        venueGamesService.getAvailableGames(filters).catch(() => []),
        gamesService.getAllGames().catch(() => [])
      ]);
      
      // Filter out past games - only show current and future games
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Start of today
      
      const filteredVenueGames = venueGamesData.filter(game => {
        const gameDate = new Date(game.game_date);
        return gameDate >= currentDate;
      });
      
      const filteredRegularGames = regularGamesData.filter(game => {
        const gameDate = new Date(game.game_date);
        // Only include games that are today or in the future
        return gameDate >= currentDate;
      });
      
      setVenueGames(filteredVenueGames);
      setRegularGames(filteredRegularGames);
    } catch (error) {
      console.error('Error loading available games:', error);
      // Only show error toast for actual system failures, not empty results
      if (error?.message && !error.message.includes('no rows returned')) {
        toast.error('Failed to load available games. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSports = async () => {
    try {
      const sports = await sportsService.getAvailableSports();
      setAvailableSports(sports);
    } catch (error) {
      console.error('Error loading sports:', error);
    }
  };

  const requestToJoinGame = async (gameId: string, teamSide: 'home' | 'away') => {
    if (!user) {
      toast.error('Please log in to join a game');
      return;
    }

    // Check if already requested
    if (userGameRequests[gameId]?.[teamSide]) {
      toast.info('You have already requested to join this team');
      return;
    }

    // Check if user has the appropriate role for team management
    if (!['coach', 'team-owner', 'venue', 'admin'].includes(permissions.role)) {
      toast.error(
        'Only coaches, team owners, and venues can manage teams. Want to upgrade your account to access these features?',
        { 
          duration: 8000,
          action: {
            label: 'Upgrade Account',
            onClick: () => window.open('/account-upgrade', '_blank')
          }
        }
      );
      return;
    }

    try {
      const game = allGames.find(g => g.id === gameId);
      if (game?.game_code) {
        toast.success(
          `To join this game as ${teamSide} team, use the game code: ${game.game_code} in your team dashboard`,
          { duration: 8000 }
        );
      }
    } catch (error) {
      console.error('Error requesting to join game:', error);
      toast.error('Failed to send join request');
    }
  };

  const requestToPlayAsPlayer = async (gameId: string, teamSide: 'home' | 'away') => {
    if (!user) {
      toast.error('Please log in to request to play');
      return;
    }

    // Check if already requested
    if (userGameRequests[gameId]?.[teamSide]) {
      toast.info('You have already requested to join this team');
      return;
    }

    // Check for under-13 restriction
    if (permissions.role === 'child' || ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13-and-under'].includes(permissions.role)) {
      toast.error('Under 13 accounts can only join teams via Kid Team Code for safety reasons');
      return;
    }

    const requestKey = `${gameId}-${teamSide}`;
    setRequestingGames(prev => ({ ...prev, [requestKey]: true }));

    try {
      console.log('🎯 Attempting to join game:', { gameId, userId, teamSide, userType: supabaseUser ? 'supabase' : 'internal' });
      
      const success = await gamesService.requestToJoinGame(
        gameId, 
        userId!, 
        teamSide, 
        supabaseUser ? 'supabase' : 'internal'
      );

      if (success) {
        toast.success(
          `Your request to play on the ${teamSide} team has been sent! Coaches and team managers will review your request.`,
          { duration: 6000 }
        );
        
        // Update local state to reflect the request
        setUserGameRequests(prev => ({
          ...prev,
          [gameId]: {
            ...prev[gameId],
            [teamSide]: true
          }
        }));
        
        // Refresh the games list to show updated status
        await loadAvailableGames();
      } else {
        console.error('❌ Join request failed for unknown reason');
        toast.error('Failed to submit join request. You may have already requested to join this game.');
      }
    } catch (error) {
      console.error('❌ Error requesting to join game:', error);
      toast.error('Failed to join game. Please check your connection and try again.');
    } finally {
      setRequestingGames(prev => {
        const newState = { ...prev };
        delete newState[requestKey];
        return newState;
      });
    }
  };

  const requestToScorekeep = async (gameId: string) => {
    if (!user) {
      toast.error('Please log in to offer scorekeeping');
      return;
    }

    // Check if user has scorekeeper role
    if (permissions.role !== 'scorekeeper') {
      toast.error(
        'Only users with a Scorekeeper role can apply for scorekeeping positions. Upgrade your account to access this feature!',
        { duration: 8000 }
      );
      return;
    }

    try {
      // TODO: Implement scorekeeper request functionality
      toast.success(
        'Your scorekeeper request has been sent! Game organizers will be notified.',
        { duration: 6000 }
      );
    } catch (error) {
      console.error('Error requesting to scorekeep:', error);
      toast.error('Failed to send scorekeeper request');
    }
  };

  // Combine and filter games from both sources
  const allGames = [
    ...venueGames,
    ...regularGames.map(game => ({
      id: game.id,
      game_title: game.game_name,
      game_date: game.game_date,
      game_time: game.game_time,
      field_location: game.venue_address || game.venue_name,
      game_description: `${game.home_team?.team_name || 'Looking for Home Team'} vs ${game.away_team?.team_name || 'Looking for Away Team'}`,
      home_team_id: game.home_team_id,
      away_team_id: game.away_team_id,
      game_code: game.game_code,
      scorekeeper_needed: false,
      scorekeeper_points: 0,
      home_team: game.home_team || { team_name: 'Looking for Home Team' },
      away_team: game.away_team || { team_name: 'Looking for Away Team' }
    }))
  ];

  const filteredGames = allGames.filter(game => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      game.game_title.toLowerCase().includes(searchLower) ||
      game.field_location?.toLowerCase().includes(searchLower) ||
      game.home_team?.team_name?.toLowerCase().includes(searchLower) ||
      game.away_team?.team_name?.toLowerCase().includes(searchLower)
    );
  });

  const formatTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return time;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Trophy className="w-6 h-6 text-primary" />
            Find Games to Join
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {/* Search and Filter Bar */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search games by title, location, or team..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-background border-border text-foreground pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/10 rounded-lg border">
              <Select
                value={filters.sport_id?.toString() || ''}
                onValueChange={(value) => setFilters(prev => ({
                  ...prev,
                  sport_id: value ? parseInt(value) : undefined
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Sports" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Sports</SelectItem>
                  {availableSports.map((sport) => (
                    <SelectItem key={sport.id} value={sport.id.toString()}>
                      {sport.sport_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                type="date"
                placeholder="From Date"
                value={filters.date_from || ''}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  date_from: e.target.value || undefined
                }))}
                min={new Date().toISOString().split('T')[0]}
              />

              <Input
                type="date"
                placeholder="To Date"
                value={filters.date_to || ''}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  date_to: e.target.value || undefined
                }))}
                min={filters.date_from || new Date().toISOString().split('T')[0]}
              />

              <Select
                value={filters.status || ''}
                onValueChange={(value) => setFilters(prev => ({
                  ...prev,
                  status: value || undefined
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Statuses</SelectItem>
                  <SelectItem value="pending">Looking for Teams</SelectItem>
                  <SelectItem value="confirmed">Confirmed Games</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Games List */}
      <Tabs defaultValue="looking-for-teams" className="space-y-4">
        <TabsList>
          <TabsTrigger value="looking-for-teams">Looking for Teams</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming Games</TabsTrigger>
        </TabsList>

        <TabsContent value="looking-for-teams" className="space-y-4">
          {filteredGames.filter(game => !game.home_team_id || !game.away_team_id).length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No games currently looking for teams</p>
              </CardContent>
            </Card>
          ) : (
            filteredGames
              .filter(game => !game.home_team_id || !game.away_team_id)
              .map((game) => (
                <Card key={game.id} className="bg-card border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg text-foreground">
                        {game.game_title}
                      </CardTitle>
                      <Badge variant="secondary">
                        {!game.home_team_id && !game.away_team_id ? 'Needs Both Teams' :
                         !game.home_team_id ? 'Needs Home Team' : 'Needs Away Team'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(game.game_date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{formatTime(game.game_time)}</span>
                      </div>
                       <div className="flex items-center gap-2 text-muted-foreground">
                         <MapPin className="h-4 w-4" />
                         <span>{(game as any).venue_name || game.field_location || 'Location TBD'}</span>
                       </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       <div className="border rounded-lg p-3">
                         <h4 className="font-medium mb-2 text-foreground">Home Team</h4>
                         {game.home_team_id ? (
                           <div className="flex items-center gap-3">
                             <TeamLogo 
                               teamName={game.home_team?.team_name} 
                               logoUrl={game.home_team?.team_logo}
                               size="md"
                             />
                             <span className="text-foreground font-medium">{game.home_team?.team_name}</span>
                           </div>
                         ) : (
                          <div className="space-y-2">
                            <p className="text-muted-foreground">Open Position</p>
                             <div className="space-y-2">
                               {userGameRequests[game.id]?.['home'] ? (
                                 <Button
                                   size="sm"
                                   disabled
                                   variant="secondary"
                                   className="bg-gray-200 text-gray-600 w-full cursor-not-allowed"
                                 >
                                   <CheckCircle2 className="w-3 h-3 mr-1" />
                                   Request Sent
                                 </Button>
                               ) : (
                                 <Button
                                   size="sm"
                                   onClick={() => requestToJoinGame(game.id, 'home')}
                                   disabled={requestingGames[`${game.id}-home`]}
                                   className="bg-primary text-primary-foreground hover:bg-primary/90 w-full"
                                 >
                                   {requestingGames[`${game.id}-home`] ? 'Sending...' : 'Request to Join as Home Team'}
                                 </Button>
                               )}
                               {userGameRequests[game.id]?.['home'] ? (
                                 <Button
                                   size="sm"
                                   disabled
                                   variant="secondary"
                                   className="bg-gray-200 text-gray-600 w-full cursor-not-allowed flex items-center gap-1"
                                 >
                                   <CheckCircle2 className="w-3 h-3" />
                                   Request Sent
                                 </Button>
                               ) : (
                                 <Button
                                   size="sm"
                                   variant="outline"
                                   onClick={() => requestToPlayAsPlayer(game.id, 'home')}
                                   disabled={requestingGames[`${game.id}-home`]}
                                   className="w-full flex items-center gap-1"
                                 >
                                   <UserCheck className="w-3 h-3" />
                                   {requestingGames[`${game.id}-home`] ? 'Sending...' : 'Request to Play'}
                                 </Button>
                               )}
                            </div>
                          </div>
                        )}
                      </div>
                       <div className="border rounded-lg p-3">
                         <h4 className="font-medium mb-2 text-foreground">Away Team</h4>
                         {game.away_team_id ? (
                           <div className="flex items-center gap-3">
                             <TeamLogo 
                               teamName={game.away_team?.team_name} 
                               logoUrl={game.away_team?.team_logo}
                               size="md"
                             />
                             <span className="text-foreground font-medium">{game.away_team?.team_name}</span>
                           </div>
                         ) : (
                          <div className="space-y-2">
                            <p className="text-muted-foreground">Open Position</p>
                             <div className="space-y-2">
                               {userGameRequests[game.id]?.['away'] ? (
                                 <Button
                                   size="sm"
                                   disabled
                                   variant="secondary"
                                   className="bg-gray-200 text-gray-600 w-full cursor-not-allowed"
                                 >
                                   <CheckCircle2 className="w-3 h-3 mr-1" />
                                   Request Sent
                                 </Button>
                               ) : (
                                 <Button
                                   size="sm"
                                   onClick={() => requestToJoinGame(game.id, 'away')}
                                   disabled={requestingGames[`${game.id}-away`]}
                                   className="bg-primary text-primary-foreground hover:bg-primary/90 w-full"
                                 >
                                   {requestingGames[`${game.id}-away`] ? 'Sending...' : 'Request to Join as Away Team'}
                                 </Button>
                               )}
                               {userGameRequests[game.id]?.['away'] ? (
                                 <Button
                                   size="sm"
                                   disabled
                                   variant="secondary"
                                   className="bg-gray-200 text-gray-600 w-full cursor-not-allowed flex items-center gap-1"
                                 >
                                   <CheckCircle2 className="w-3 h-3" />
                                   Request Sent
                                 </Button>
                               ) : (
                                 <Button
                                   size="sm"
                                   variant="outline"
                                   onClick={() => requestToPlayAsPlayer(game.id, 'away')}
                                   disabled={requestingGames[`${game.id}-away`]}
                                   className="w-full flex items-center gap-1"
                                 >
                                   <UserCheck className="w-3 h-3" />
                                   {requestingGames[`${game.id}-away`] ? 'Sending...' : 'Request to Play'}
                                 </Button>
                               )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {game.game_description && (
                      <p className="text-muted-foreground text-sm">{game.game_description}</p>
                    )}

                    {(game.scorekeeper_needed || true) && (
                      <div className="flex items-center justify-between bg-muted/10 p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Users className="h-4 w-4" />
                          <span>Scorekeeper needed ({game.scorekeeper_points || 50} points)</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => requestToScorekeep(game.id)}
                          className="flex items-center gap-1"
                          disabled={permissions.role !== 'scorekeeper'}
                        >
                          <Star className="w-3 h-3" />
                          {permissions.role === 'scorekeeper' ? 'Scorekeep this Game' : 'Scorekeeper Only'}
                        </Button>
                      </div>
                    )}

                    {game.game_code && (
                      <div className="bg-muted/20 p-3 rounded-lg">
                        <p className="text-sm text-muted-foreground">Game Code:</p>
                        <p className="font-mono font-bold text-lg text-foreground">{game.game_code}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Share this code with your team to join the game
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
          )}
        </TabsContent>

        <TabsContent value="upcoming" className="space-y-4">
          {filteredGames.filter(game => game.home_team_id && game.away_team_id).length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No upcoming confirmed games</p>
              </CardContent>
            </Card>
          ) : (
            filteredGames
              .filter(game => game.home_team_id && game.away_team_id)
              .map((game) => (
                <Card key={game.id} className="bg-card border-border">
                  <CardHeader>
                     <div className="flex items-center justify-between">
                       <div className="flex items-center gap-4">
                         <div className="flex items-center gap-2">
                           <TeamLogo 
                             teamName={game.home_team?.team_name} 
                             logoUrl={game.home_team?.team_logo}
                             size="sm"
                           />
                           <span className="font-medium">{game.home_team?.team_name}</span>
                         </div>
                         <span className="text-muted-foreground">vs</span>
                         <div className="flex items-center gap-2">
                           <TeamLogo 
                             teamName={game.away_team?.team_name} 
                             logoUrl={game.away_team?.team_logo}
                             size="sm"
                           />
                           <span className="font-medium">{game.away_team?.team_name}</span>
                         </div>
                       </div>
                       <Badge variant="default">Confirmed</Badge>
                     </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {game.game_code && (
                      <div className="bg-muted/20 p-3 rounded-lg">
                        <p className="text-sm text-muted-foreground">Game Code:</p>
                        <p className="font-mono font-bold text-lg text-foreground">{game.game_code}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Share this code with your team to join the game
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(game.game_date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{formatTime(game.game_time)}</span>
                      </div>
                       <div className="flex items-center gap-2 text-muted-foreground">
                         <MapPin className="h-4 w-4" />
                         <span>{(game as any).venue_name || game.field_location || 'Location TBD'}</span>
                       </div>
                    </div>

                    {game.game_description && (
                      <p className="text-muted-foreground text-sm">{game.game_description}</p>
                    )}
                  </CardContent>
                </Card>
              ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};